import { useState } from 'react';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { CHECKLIST_SECTIONS } from '../data/checklistData';
import { createPdf, PDF_COLORS as colors, SITE_URL } from '../utils/pdfCore';
import {
  FileDown,
  X,
  Loader2,
  Printer,
  ClipboardList,
  FileText,
  Zap,
} from 'lucide-react';

const PrintableChecklist = ({ onClose }) => {
  const { showToast } = usePDIContext();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState(null);

  // ========== COMPACT 2-PAGE VERSION ==========
  const generateCompactChecklist = async () => {
    setIsGenerating(true);
    setGeneratingType('compact');

    try {
      const pdf = await createPdf({
        margin: 8,
        footerLeft: 'P=Pass  F=Fail  A=Attention  |  (!) = Critical Item',
      });
      const { doc, pageWidth, margin, contentWidth } = pdf;

      // ========== PAGE 1 ==========
      // Compact Header
      pdf.rect(margin, pdf.y, contentWidth, 14, 2, colors.headerBg);
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Car PDI Checklist - Quick Reference', pageWidth / 2, pdf.y + 6, { align: 'center' });
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text('Pre-Delivery Inspection | P=Pass  F=Fail  A=Attention', margin + 4, pdf.y + 11);
      doc.setTextColor(120, 220, 150);
      doc.text(SITE_URL, pageWidth - margin - 4, pdf.y + 11, { align: 'right' });
      pdf.y += 17;

      // Vehicle Info (compact single line)
      doc.setFontSize(7);
      doc.setTextColor(...colors.medium);
      doc.text('Car: ________________  VIN: ________________  Date: ________  Dealer: ________________', margin, pdf.y);
      pdf.y += 5;

      // Get all critical items
      const criticalItems = [];
      CHECKLIST_SECTIONS.forEach(section => {
        section.items.forEach(item => {
          if (item.critical) {
            criticalItems.push({ ...item, sectionTitle: section.title });
          }
        });
      });

      // Critical Items Section (compact)
      pdf.rect(margin, pdf.y, contentWidth, 6, 1, colors.dangerLight);
      doc.setFontSize(8);
      doc.setTextColor(...colors.critical);
      doc.text(`CRITICAL ITEMS (${criticalItems.length}) - Do NOT accept if failed`, margin + 2, pdf.y + 4);
      pdf.y += 8;

      // Two columns for critical items
      const colWidth = (contentWidth - 4) / 2;
      const itemsPerCol = Math.ceil(criticalItems.length / 2);

      criticalItems.forEach((item, i) => {
        const col = i < itemsPerCol ? 0 : 1;
        const row = i < itemsPerCol ? i : i - itemsPerCol;
        const x = margin + (col * (colWidth + 4));
        const itemY = pdf.y + (row * 5);

        pdf.checkbox(x, itemY, 3);
        pdf.checkbox(x + 4, itemY, 3);
        pdf.checkbox(x + 8, itemY, 3);

        doc.setFontSize(6);
        doc.setTextColor(...colors.dark);
        const titleText = item.title.length > 35 ? item.title.substring(0, 35) + '...' : item.title;
        doc.text(titleText, x + 13, itemY + 2.5);
      });

      pdf.y += (itemsPerCol * 5) + 4;

      // All Sections in compact format
      CHECKLIST_SECTIONS.forEach((section, sectionIdx) => {
        const sectionHeight = 6 + Math.ceil(section.items.length / 2) * 4.5;
        pdf.newPageIfNeeded(sectionHeight);

        // Section header
        pdf.rect(margin, pdf.y, contentWidth, 5, 1, colors.headerBg);
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text(`${sectionIdx + 1}. ${section.title} (${section.items.length})`, margin + 2, pdf.y + 3.5);
        pdf.y += 6;

        // Items in two columns
        const sectionColWidth = (contentWidth - 2) / 2;
        const sectionItemsPerCol = Math.ceil(section.items.length / 2);

        section.items.forEach((item, i) => {
          const col = i < sectionItemsPerCol ? 0 : 1;
          const row = i < sectionItemsPerCol ? i : i - sectionItemsPerCol;
          const x = margin + (col * (sectionColWidth + 2));
          const itemY = pdf.y + (row * 4.5);

          pdf.checkbox(x, itemY, 2.5);
          pdf.checkbox(x + 3.5, itemY, 2.5);
          pdf.checkbox(x + 7, itemY, 2.5);

          doc.setFontSize(5.5);
          doc.setTextColor(...colors.dark);
          const prefix = item.critical ? '(!) ' : '';
          let title = prefix + item.title;
          if (title.length > 40) title = title.substring(0, 40) + '...';
          doc.text(title, x + 11, itemY + 2);
        });

        pdf.y += (sectionItemsPerCol * 4.5) + 3;
      });

      pdf.footer();

      const date = new Date().toISOString().split('T')[0];
      doc.save(`Car_PDI_Checklist_Compact_${date}.pdf`);
      track('downloaded_printable', { format: 'compact' });
      showToast('Checklist downloaded');
    } catch (error) {
      console.error('Error generating compact checklist:', error);
      showToast('Failed to generate checklist');
    } finally {
      setIsGenerating(false);
      setGeneratingType(null);
    }
  };

  // ========== DETAILED FULL VERSION ==========
  const generateDetailedChecklist = async () => {
    setIsGenerating(true);
    setGeneratingType('detailed');

    try {
      const pdf = await createPdf({ margin: 12, footerLeft: 'Car PDI Checklist' });
      const { doc, pageWidth, pageHeight, margin, contentWidth } = pdf;

      // ========== COVER PAGE ==========
      pdf.rect(0, 0, pageWidth, 55, 0, colors.headerBg);

      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('Car PDI Checklist', pageWidth / 2, 25, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(200, 200, 200);
      doc.text('Pre-Delivery Inspection Guide', pageWidth / 2, 35, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(120, 220, 150);
      doc.text(SITE_URL, pageWidth / 2, 45, { align: 'center' });

      pdf.y = 65;

      // Quick Guide Box
      pdf.rect(margin, pdf.y, contentWidth, 45, 3, colors.sectionBg);
      doc.setFontSize(11);
      doc.setTextColor(...colors.dark);
      doc.text('How to Use This Checklist', margin + 5, pdf.y + 8);

      doc.setFontSize(8);
      doc.setTextColor(...colors.medium);
      const instructions = [
        '1. Print this checklist before visiting the dealer',
        '2. Bring a pen and a flashlight (phone light works)',
        '3. Take your time - a thorough PDI takes 30-45 minutes',
        '4. Mark each item: Pass, Fail, or Needs Attention',
        '5. Items marked with (!) are CRITICAL - don\'t accept delivery if failed',
        '6. Write notes for any issues found',
        '7. If issues are found, ask dealer to fix before accepting delivery',
      ];
      instructions.forEach((inst, i) => {
        doc.text(inst, margin + 8, pdf.y + 16 + (i * 4));
      });

      pdf.y += 55;

      // Vehicle Details Box
      pdf.rect(margin, pdf.y, contentWidth, 35, 3, colors.white);
      pdf.strokeRect(margin, pdf.y, contentWidth, 35, 3, colors.light);

      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.text('Vehicle Details (Fill Before Inspection)', margin + 5, pdf.y + 7);

      doc.setFontSize(8);
      doc.setTextColor(...colors.medium);
      doc.text('Car Make/Model: ________________________________', margin + 8, pdf.y + 14);
      doc.text('Color: ________________  Variant: ________________', margin + 8, pdf.y + 20);
      doc.text('VIN: _______________________________________', margin + 8, pdf.y + 26);
      doc.text('Dealer: ________________  Date: ________________', margin + 8, pdf.y + 32);

      pdf.y += 45;

      // Legend
      pdf.rect(margin, pdf.y, contentWidth, 18, 2, colors.tipBg);
      doc.setFontSize(9);
      doc.setTextColor(...colors.dark);
      doc.text('Status Legend:', margin + 5, pdf.y + 6);

      const legendY = pdf.y + 11;
      pdf.checkbox(margin + 10, legendY, 4, 'PASS - Item is OK');
      pdf.checkbox(margin + 50, legendY, 4, 'FAIL - Has issues');
      pdf.checkbox(margin + 95, legendY, 4, 'ATT - Needs attention');

      // ========== SECTION PAGES ==========
      CHECKLIST_SECTIONS.forEach((section, sectionIndex) => {
        pdf.addPage();

        // Section Header
        pdf.rect(margin, pdf.y, contentWidth, 20, 2, colors.headerBg);

        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.text(`${sectionIndex + 1}. ${section.title}`, margin + 5, pdf.y + 9);

        doc.setFontSize(8);
        doc.setTextColor(180, 180, 180);
        doc.text(section.description, margin + 5, pdf.y + 15);

        if (section.critical) {
          doc.setFontSize(7);
          doc.setTextColor(255, 200, 200);
          doc.text('Contains critical items', pageWidth - margin - 5, pdf.y + 9, { align: 'right' });
        }

        pdf.y += 26;

        // Items
        section.items.forEach((item, itemIndex) => {
          let requiredSpace = 22;
          if (item.tip) requiredSpace += 6;
          if (item.warning) requiredSpace += 6;

          pdf.newPageIfNeeded(requiredSpace);

          const itemHeight = requiredSpace - 4;
          const bgColor = item.critical ? colors.criticalLight : colors.sectionBg;
          pdf.rect(margin, pdf.y, contentWidth, itemHeight, 2, bgColor);

          if (item.critical) {
            doc.setFillColor(...colors.critical);
            doc.rect(margin, pdf.y, 2, itemHeight, 'F');
          }

          doc.setFontSize(10);
          doc.setTextColor(...colors.dark);
          const titlePrefix = item.critical ? '(!) ' : '';
          doc.text(`${itemIndex + 1}. ${titlePrefix}${item.title}`, margin + 5, pdf.y + 6);

          const checkboxY = pdf.y + 2;
          pdf.checkbox(pageWidth - margin - 38, checkboxY, 5, 'P');
          pdf.checkbox(pageWidth - margin - 26, checkboxY, 5, 'F');
          pdf.checkbox(pageWidth - margin - 14, checkboxY, 5, 'A');

          doc.setFontSize(8);
          doc.setTextColor(...colors.medium);
          const descLines = doc.splitTextToSize(item.description, contentWidth - 50);
          descLines.forEach((line, i) => {
            doc.text(line, margin + 5, pdf.y + 11 + (i * 3.5));
          });

          let innerY = pdf.y + 11 + (descLines.length * 3.5) + 2;

          if (item.tip) {
            doc.setFontSize(7);
            doc.setTextColor(22, 163, 74);
            const tipLines = doc.splitTextToSize(`Tip: ${item.tip}`, contentWidth - 12);
            tipLines.forEach((line, i) => {
              doc.text(line, margin + 8, innerY + (i * 3));
            });
            innerY += tipLines.length * 3 + 1;
          }

          if (item.warning) {
            doc.setFontSize(7);
            doc.setTextColor(202, 138, 4);
            const warnLines = doc.splitTextToSize(`Warning: ${item.warning}`, contentWidth - 12);
            warnLines.forEach((line, i) => {
              doc.text(line, margin + 8, innerY + (i * 3));
            });
          }

          pdf.y += itemHeight + 3;
        });

        if (pdf.y < pageHeight - 40) {
          pdf.rect(margin, pdf.y + 5, contentWidth, 20, 2, colors.white);
          pdf.strokeRect(margin, pdf.y + 5, contentWidth, 20, 2, colors.light);
          doc.setFontSize(8);
          doc.setTextColor(...colors.medium);
          doc.text('Notes for this section:', margin + 3, pdf.y + 11);
        }
      });

      // ========== FINAL NOTES PAGE ==========
      pdf.addPage();

      pdf.rect(margin, pdf.y, contentWidth, 16, 2, colors.headerBg);
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('PDI Summary & Notes', margin + 5, pdf.y + 11);
      pdf.y += 24;

      pdf.rect(margin, pdf.y, contentWidth, 25, 3, colors.sectionBg);
      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.text('Overall PDI Result:', margin + 5, pdf.y + 8);

      pdf.checkbox(margin + 10, pdf.y + 14, 5, 'READY FOR DELIVERY - All items passed');
      pdf.checkbox(margin + 10, pdf.y + 20, 5, 'NEEDS ATTENTION - Minor issues to address');
      pdf.checkbox(pageWidth / 2, pdf.y + 14, 5, 'DO NOT ACCEPT - Critical issues found');

      pdf.y += 32;

      pdf.rect(margin, pdf.y, contentWidth, 50, 3, colors.criticalLight);
      doc.setFontSize(10);
      doc.setTextColor(...colors.critical);
      doc.text('Issues Found (List failed items here):', margin + 5, pdf.y + 8);

      for (let i = 0; i < 6; i++) {
        doc.setDrawColor(...colors.light);
        doc.line(margin + 5, pdf.y + 15 + (i * 6), pageWidth - margin - 5, pdf.y + 15 + (i * 6));
      }

      pdf.y += 58;

      pdf.rect(margin, pdf.y, contentWidth, 35, 3, colors.white);
      pdf.strokeRect(margin, pdf.y, contentWidth, 35, 3, colors.dark);

      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.text('Dealer Acknowledgment', margin + 5, pdf.y + 8);

      doc.setFontSize(8);
      doc.setTextColor(...colors.medium);
      doc.text('The above issues have been noted and will be rectified before delivery.', margin + 5, pdf.y + 15);
      doc.text('Dealer Representative: _________________________  Date: ______________', margin + 5, pdf.y + 23);
      doc.text('Customer Signature: ____________________________  Date: ______________', margin + 5, pdf.y + 30);

      pdf.y += 42;

      pdf.rect(margin, pdf.y, contentWidth, 40, 3, colors.tipBg);
      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.text('Quick Tips for Negotiating Fixes', margin + 5, pdf.y + 8);

      doc.setFontSize(7);
      doc.setTextColor(...colors.medium);
      const tips = [
        '• Be polite but firm - you have the right to a defect-free vehicle',
        '• Take photos/videos of all issues as evidence',
        '• Ask for issues to be fixed IN WRITING before accepting delivery',
        '• Don\'t accept verbal promises - get everything documented',
        '• If dealer refuses to fix critical issues, escalate to manufacturer helpline',
        '• You can refuse delivery until issues are resolved without penalty',
        '• Keep copies of all documents - invoice, warranty card, insurance',
      ];
      tips.forEach((tip, i) => {
        doc.text(tip, margin + 5, pdf.y + 15 + (i * 4));
      });

      pdf.footer();

      const date = new Date().toISOString().split('T')[0];
      doc.save(`Car_PDI_Checklist_Detailed_${date}.pdf`);
      track('downloaded_printable', { format: 'detailed' });
      showToast('Checklist downloaded');
    } catch (error) {
      console.error('Error generating detailed checklist:', error);
      showToast('Failed to generate checklist');
    } finally {
      setIsGenerating(false);
      setGeneratingType(null);
    }
  };

  const totalItems = CHECKLIST_SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="modal-backdrop flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-lg">Offline Checklist</h2>
              <p className="text-xs text-slate-300 mt-0.5">Choose your format</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 -m-2 text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Compact Version Option */}
          <button
            onClick={generateCompactChecklist}
            disabled={isGenerating}
            className="w-full p-4 bg-gradient-to-br from-brand-50 to-teal-50 border-2 border-brand-200 rounded-xl text-left hover:border-brand-400 hover:shadow-lg transition-all group disabled:opacity-50"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                {isGenerating && generatingType === 'compact' ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Zap className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800">Quick Reference</h3>
                  <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-xs font-bold rounded-full">2 Pages</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Compact checklist with all {totalItems} items. Just checkboxes and item names - perfect for quick inspections.
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" />
                    All items
                  </span>
                  <span className="flex items-center gap-1">
                    <FileDown className="w-3 h-3" />
                    2 pages
                  </span>
                </div>
              </div>
            </div>
          </button>

          {/* Detailed Version Option */}
          <button
            onClick={generateDetailedChecklist}
            disabled={isGenerating}
            className="w-full p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl text-left hover:border-amber-400 hover:shadow-lg transition-all group disabled:opacity-50"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                {isGenerating && generatingType === 'detailed' ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <FileText className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800">Detailed Guide</h3>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">Full</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Complete checklist with descriptions, tips, warnings, and notes section for each category.
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" />
                    Tips & guidance
                  </span>
                  <span className="flex items-center gap-1">
                    <FileDown className="w-3 h-3" />
                    18 pages
                  </span>
                </div>
              </div>
            </div>
          </button>

          {/* Info */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-xs text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-700">Quick Reference:</span> Best for experienced users who know what to check. Fits in your pocket.
            </p>
            <p className="text-xs text-slate-600 leading-relaxed mt-2">
              <span className="font-semibold text-slate-700">Detailed Guide:</span> Best for first-time buyers. Includes where to look and what to look for.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableChecklist;
